/**
 * How-to guides: one data set, two renderers, like src/lib/compare.ts.
 *
 * web/src/pages/Guide.tsx draws these for people; src/lib/prerender.ts writes
 * the same text into the served HTML for crawlers.
 *
 * These are written to be useful to someone who never signs up: the advice
 * holds whichever tool they pick, and Eventgram appears where it genuinely
 * does the step being described. Every product claim must match what the
 * product does today (albums: LIMITS.maxAlbumsPerEvent; print sheet: six
 * cards per page; guest languages: lib/i18n.ts; upload window: 14 days after
 * the event date; per-phone limit: LIMITS.uploadsPerGuest per hour).
 */

export interface GuideSection {
  readonly heading: string;
  readonly paragraphs: readonly string[];
  readonly list?: readonly string[];
}

export interface Guide {
  readonly slug: string;
  readonly title: string;
  readonly description: string;
  readonly eyebrow: string;
  readonly h1: string;
  readonly intro: string;
  readonly sections: readonly GuideSection[];
}

export const GUIDES: readonly Guide[] = [
  {
    slug: "wedding-photo-qr-code",
    title: "QR Code for Wedding Photos: Collect Every Guest's Pictures | Eventgram",
    description:
      "How to set up a QR code so wedding guests can share their photos and videos in one album: where to put it, what to print, and the mistakes to avoid.",
    eyebrow: "Wedding guide",
    h1: "How to use a QR code to collect your wedding photos",
    intro:
      "Your photographer gets the posed shots. Your guests get everything else: the speeches from the back of the room, the dance floor at midnight, the moments you were too busy to see. A QR code is the simplest way to get all of it into one place, in full quality, without asking anyone to install an app.",
    sections: [
      {
        heading: "How it works",
        paragraphs: [
          "You create a shared album and get a QR code for it. Guests point their phone camera at the code, a web page opens, and they pick photos and videos from their camera roll. Everything lands in one private album you can download afterwards.",
        ],
        list: [
          "No app and no account for guests: a phone camera and a browser are enough.",
          "Photos arrive at full resolution, unlike photos forwarded through messaging apps, which are compressed.",
          "Videos work too, which matters for speeches and first dances.",
        ],
      },
      {
        heading: "Where to put the QR code",
        paragraphs: [
          "One sign at the entrance is not enough: guests walk past it before they have taken a single photo. Put the code where people are when they have their phone out.",
        ],
        list: [
          "On every table, as a small table card or tent card.",
          "At the bar and by the dance floor.",
          "On the back of the order of service or the menu.",
          "In the thank-you message after the wedding, for photos people forgot to share on the day.",
        ],
      },
      {
        heading: "Get guests to actually use it",
        paragraphs: [
          "The biggest difference to how many photos you get is one announcement. Ask your DJ, band or emcee to mention the QR code once during dinner, and again before the dancing starts.",
          "Put a short link under every QR code too, for the guest whose camera will not scan it.",
        ],
      },
      {
        heading: "Show the photos live",
        paragraphs: [
          "If the venue has a TV or projector, put the album on it. New photos appear as guests upload them, and seeing their own photo on the big screen is what makes everyone else reach for their phone.",
          "If you want to check photos before they appear, turn on moderation: you approve each upload from your phone first.",
        ],
      },
      {
        heading: "Mistakes to avoid",
        paragraphs: [],
        list: [
          "Testing the code for the first time on the day. Scan it on two or three different phones the week before.",
          "Printing the QR code too small. About 3 cm (just over an inch) square is a sensible minimum for a table card.",
          "Relying on venue Wi-Fi alone. Check the mobile signal in the room; a failed upload can be retried, but most guests will not bother.",
          "Choosing a tool with a guest limit lower than your guest list, or an album that is deleted after a few weeks.",
        ],
      },
      {
        heading: "Doing this with Eventgram",
        paragraphs: [
          "Create a free event to try it (up to 15 guests), then pick a one-time pass for the wedding itself: Wedding is $49 for 25 GB, about 6,000 photos, with no charge per guest. Eventgram prints six table cards per sheet, runs the live wall on any screen with a browser, and shows guests the upload page in English, Spanish, French, German, Italian or Portuguese.",
        ],
      },
    ],
  },
  {
    slug: "south-asian-wedding-photo-sharing",
    title: "Collect Guest Photos From Every Event of a South Asian Wedding | Eventgram",
    description:
      "Mehndi, sangeet, haldi, baraat, ceremony and reception: how to collect every guest's photos from a multi-day South Asian wedding into one album, sorted by event.",
    eyebrow: "Wedding guide",
    h1: "Collect guest photos from every event of a South Asian wedding",
    intro:
      "A South Asian wedding is not one day. The mehndi, sangeet, haldi, baraat, ceremony and reception can run across a long weekend, often at different venues, with different guests at each. Your photographer cannot be at the haldi and the bar at the same time. Your guests already are.",
    sections: [
      {
        heading: "The problem with WhatsApp groups",
        paragraphs: [
          "The usual answer is a family WhatsApp group, and the usual result is hundreds of compressed photos scattered across chats, with no way to find the sangeet photos again a month later.",
          "What works better is one album with a section for each event, filled by the guests themselves, in full quality.",
        ],
      },
      {
        heading: "One QR code, an album for each event",
        paragraphs: [
          "Set up one shared album for the whole wedding and create a sub-album for each event. Guests scan the same QR code all weekend and choose which event their photos are from as they upload. You print one design and use it everywhere.",
        ],
        list: [
          "Mehndi and haldi: candid, close-up, mostly phone photos. Often the events with the fewest professional shots.",
          "Sangeet: performances are where guest videos matter most.",
          "Baraat: outdoors and moving, so put the code on a sign at the starting point, not only on tables.",
          "Ceremony and reception: the code on every table, plus a mention from the emcee.",
        ],
      },
      {
        heading: "Practical tips for a multi-day wedding",
        paragraphs: [],
        list: [
          "Print the QR code on the welcome card or itinerary that out-of-town guests receive, so they have it for every event.",
          "Ask a cousin to own it: one person reminding people at each event does more than any sign.",
          "Older relatives may not scan QR codes. Share the same link in the family WhatsApp group so they can tap it instead.",
          "Plan for video. Sangeet performances and the baraat produce large files; make sure the album has room for them.",
        ],
      },
      {
        heading: "Doing this with Eventgram",
        paragraphs: [
          "Eventgram gives each event up to 20 albums under one QR code, and guests pick the album as they upload. For a full wedding weekend the Signature pass ($79, one-time) holds 50 GB, about 12,500 photos, and keeps them for three years. There is no charge per guest, and a free event lets you test it at the mehndi first.",
        ],
      },
    ],
  },
  {
    slug: "holiday-party-photo-qr-code",
    title: "Holiday Party Photo QR Code: Collect Everyone's Pictures | Eventgram",
    description:
      "Set up a QR code for your Christmas, office or New Year's Eve party so every guest's photos land in one album, live on the screen. Takes ten minutes.",
    eyebrow: "Party guide",
    h1: "A QR code for your holiday party photos",
    intro:
      "Office Christmas parties, family gatherings and New Year's Eve all end the same way: everyone took photos, and nobody can find them in January. A QR code on the tables fixes that in about ten minutes of setup.",
    sections: [
      {
        heading: "Set it up in ten minutes",
        paragraphs: [],
        list: [
          "Create a shared album for the party and download its QR code.",
          "Print it on table cards, or put it on the screen at the start of the night.",
          "Guests scan it with their phone camera and upload. No app, no sign-up.",
          "After the party, download everything as one ZIP or send everyone the gallery link.",
        ],
      },
      {
        heading: "Put the photos on the big screen",
        paragraphs: [
          "If there is a TV or projector, show the album on it. Photos appear as they are uploaded, which turns the screen into the thing people gather around.",
          "For an office party, turn on moderation so you approve each photo before it appears.",
        ],
      },
      {
        heading: "Keep the gallery private",
        paragraphs: [
          "Holiday party photos are usually not for the public internet. Use a gallery that is only reachable by its link, and add a password if the photos are for staff only.",
        ],
      },
      {
        heading: "Doing this with Eventgram",
        paragraphs: [
          "A free event covers a small gathering of up to 15 guests. For a bigger party, the Party pass is $29 one-time for 5 GB, about 1,200 photos, kept for a year, with a live screen wall, moderation and an optional gallery password.",
        ],
      },
    ],
  },
];

export function guideFor(pathname: string): Guide | null {
  const slug = pathname.replace(/^\/+|\/+$/g, "");
  return GUIDES.find((g) => g.slug === slug) ?? null;
}
